// Stage 4 — Operations (FR-4.*). Batch CSV, Janeway handoff, review tracking + escalation,
// publishing, and the in-app productivity report (replaces the manual Google Sheet).
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DOMAINS, preprintStatusLabel, type Domain } from "@rrid/shared-types";
import { api } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { useAuth } from "../../lib/auth.js";
import { useCan } from "../../components/Can.js";
import { Badge, Button, Card, CardBody, CardHeader, EmptyState, Field, Input, Modal, Select, Spinner, statusTone } from "../../components/ui.js";

interface OpsPreprint {
  id: string;
  title: string;
  domain: Domain | null;
  status: string;
  greenlitCount: number;
  reviewsReceived: number;
  invitesRequired: number;
  moreInvitesNeeded: boolean;
}

export function OperationsPortal(): JSX.Element {
  // July-8 FR-N4: Operations is Manager / Central / Admin. The nav entry is hidden for everyone
  // else, so this only catches a direct URL visit — the API returns 403 either way.
  const can = useCan();
  if (!can("openOperations")) {
    return (
      <EmptyState>
        You don’t have Operations access. Batching, Janeway handoff and publishing are handled by
        managers and editorial staff.
      </EmptyState>
    );
  }
  return <OperationsDashboard />;
}

function OperationsDashboard(): JSX.Element {
  const qc = useQueryClient();
  const can = useCan();
  const { user } = useAuth();
  const [domain, setDomain] = useState<Domain | "">(user?.domains[0] ?? "");
  const [publishing, setPublishing] = useState<OpsPreprint | null>(null);

  const params = new URLSearchParams();
  if (domain) params.set("domain", domain);
  const { data, isLoading } = useQuery({
    queryKey: qk.operations({ domain: domain || "all" }),
    queryFn: () => api.get<OpsPreprint[]>(`/operations?${params.toString()}`),
  });

  const janeway = useMutation({
    mutationFn: (id: string) => api.post(`/operations/${id}/janeway`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["operations"] }),
  });
  const batch = useMutation({
    mutationFn: (id: string) => api.post<{ sent: boolean; csv: string; batch?: number }>(`/operations/${id}/batch-csv`),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["operations"] });
      window.alert(res.sent ? `Batch ${res.batch} CSV emailed to ops.` : "No new batch due yet.\n\nCurrent CSV:\n" + res.csv.slice(0, 500));
    },
  });
  const review = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: string }) =>
      api.post(`/operations/review`, { preprintId: id, reviewerEmail: "reviewer@example.edu", decision }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["operations"] }),
  });

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Operations</h1>
          <p className="text-sm text-slate-500">Invitations, review tracking, escalation, and publishing (FR-4).</p>
        </div>
        <Select value={domain} onChange={(e) => setDomain(e.target.value as Domain | "")}>
          <option value="">All domains</option>
          {DOMAINS.map((d) => (
            <option key={d}>{d}</option>
          ))}
        </Select>
      </header>

      {isLoading ? (
        <Spinner label="Loading…" />
      ) : (data ?? []).length === 0 ? (
        <EmptyState>No preprints in the invitation/publish stages.</EmptyState>
      ) : (
        <div className="space-y-2">
          {(data ?? []).map((p) => (
            <Card key={p.id}>
              <CardBody className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Badge tone={statusTone(p.status)}>{preprintStatusLabel(p.status)}</Badge>
                    {p.domain && <Badge tone="blue">{p.domain}</Badge>}
                    {p.moreInvitesNeeded && <Badge tone="amber">needs invites</Badge>}
                  </div>
                  <h3 className="mt-1 font-medium">{p.title}</h3>
                  <p className="text-xs text-slate-500">
                    greenlit {p.greenlitCount} / {p.invitesRequired} required · reviews {p.reviewsReceived}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {can("sendJanewayInvites", { domain: p.domain }) && (
                    <>
                      <Button variant="secondary" className="py-1" onClick={() => batch.mutate(p.id)}>
                        Batch CSV
                      </Button>
                      <Button className="py-1" onClick={() => janeway.mutate(p.id)} disabled={p.greenlitCount === 0}>
                        Janeway handoff
                      </Button>
                    </>
                  )}
                  {can("recordSubmittedReviews", { domain: p.domain }) && (
                    <Button variant="secondary" className="py-1" onClick={() => review.mutate({ id: p.id, decision: "submitted" })}>
                      + Review in
                    </Button>
                  )}
                  {can("publishToPubPubBlueSky", { domain: p.domain }) && (
                    <Button variant="ghost" className="py-1" onClick={() => setPublishing(p)}>
                      Publish
                    </Button>
                  )}
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      <ProductivityReport canView={can("viewAuditTrail")} />
      {publishing && <PublishModal preprint={publishing} onClose={() => setPublishing(null)} />}
    </div>
  );
}

function ProductivityReport({ canView }: { canView: boolean }): JSX.Element | null {
  const { data } = useQuery({
    queryKey: qk.reports,
    queryFn: () =>
      api.get<{
        domainPerformance: { id: string; domain: string | null; metrics: Record<string, unknown> }[];
        topContributors: { email: string | null; name: string | null; totalGreenlit: number; totalProposed: number; qcCount: number }[];
      }>("/reports"),
    enabled: canView,
  });
  if (!canView) return null;
  return (
    <Card>
      <CardHeader>
        <h2 className="text-sm font-semibold">Productivity report</h2>
        <p className="text-xs text-slate-500">Replaces the manual Google Sheet (FR-4.10).</p>
      </CardHeader>
      <CardBody>
        <h3 className="mb-1 text-xs font-semibold text-slate-500">Top contributors</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-400">
              <th className="py-1">Name</th>
              <th>Greenlit</th>
              <th>Proposed</th>
              <th>QC</th>
            </tr>
          </thead>
          <tbody>
            {(data?.topContributors ?? []).slice(0, 10).map((c) => (
              <tr key={c.email ?? c.name ?? Math.random()} className="border-t border-slate-100">
                <td className="py-1">{c.name ?? c.email}</td>
                <td>{c.totalGreenlit}</td>
                <td>{c.totalProposed}</td>
                <td>{c.qcCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardBody>
    </Card>
  );
}

function PublishModal({ preprint, onClose }: { preprint: OpsPreprint; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const [f, setF] = useState({ pubPubUrl: "", blueSkyUrl: "", blurb: "" });
  const publish = useMutation({
    mutationFn: () => api.post(`/operations/publish`, { preprintId: preprint.id, ...f }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["operations"] });
      onClose();
    },
  });
  return (
    <Modal open onClose={onClose} title={`Publish — ${preprint.title}`}>
      <div className="space-y-3">
        <Field label="PubPub URL">
          <Input value={f.pubPubUrl} onChange={(e) => setF({ ...f, pubPubUrl: e.target.value })} placeholder="https://pubpub.org/…" />
        </Field>
        <Field label="BlueSky URL">
          <Input value={f.blueSkyUrl} onChange={(e) => setF({ ...f, blueSkyUrl: e.target.value })} placeholder="https://bsky.app/…" />
        </Field>
        <Field label="Blurb / one-liner">
          <Input value={f.blurb} onChange={(e) => setF({ ...f, blurb: e.target.value })} />
        </Field>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => publish.mutate()} disabled={publish.isPending}>
            Publish &amp; notify
          </Button>
        </div>
      </div>
    </Modal>
  );
}
